import {
  Component,
  ElementRef,
  HostListener,
  Inject,
  PLATFORM_ID,
  ViewChild,
  signal
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { NgbModal, NgbTooltip, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { takeUntil } from 'rxjs';
import { AiChatService, AuthService } from '@coreApiServices';
import { CommonConstants, ROUTES, STORAGE } from '@coreConstant';
import { SubscribersService, ToastService } from '@coreServices';
import { HandleImageErrorDirective } from '../../directives/handle-image-error.directive';
import { AiReplyEntriesPipe } from '../../pipes/ai-reply-entries.pipe';
import { AiProductCardsComponent } from './ai-product-cards/ai-product-cards.component';
import { AiProductEntryComponent } from './ai-product-entry/ai-product-entry.component';
import { AiChatConversationStore } from '../../services/ai-chat-conversation.store';
import { AiChatConversationBase } from './ai-chat-conversation.base';

/**
 * Floating Pebbl AI chat.
 *
 * Renders a tab bound to the right edge of every page and, when opened, a chat panel that slides
 * out of that edge with the tab riding on its side as the Close handle.
 * Guests see the same tab and panel, but the panel holds only a sign-in card - no transcript,
 * starters or composer - and signing in turns it into the chat in place.
 *
 * Mounted once in LayoutComponent. The conversation itself - streaming, tool progress, errors -
 * lives in AiChatConversationBase, shared with the full-page /ask-pebbl surface.
 */
@Component({
  selector: 'app-ai-chat',
  standalone: true,
  imports: [FormsModule, RouterLink, HandleImageErrorDirective, AiReplyEntriesPipe, AiProductCardsComponent, AiProductEntryComponent, NgbTooltipModule],
  templateUrl: './ai-chat.component.html',
  styleUrl: './ai-chat.component.scss'
})
export class AiChatComponent extends AiChatConversationBase {
  /* ################### Variable Declaration Start ################### */
  /** Whether the panel is expanded. */
  isOpen = signal(false);

  /** Guests get the sign-in card in place of the conversation. */
  isUserAuthenticated = false;
  protected readonly ROUTES = ROUTES;
  protected readonly AUTH_MODAL_TYPES = CommonConstants.AUTH_MODAL_TYPES;
  protected readonly isPlatformBrowser = isPlatformBrowser;

  @ViewChild('toggleButton') private toggleButton?: ElementRef<HTMLButtonElement>;
  /** The sign-in card's button - where focus lands when a guest opens the panel. */
  @ViewChild('guestSignIn') private guestSignIn?: ElementRef<HTMLButtonElement>;
  /**
   * The tab's "Close chat" tooltip. The tab moves when the panel opens and closes, so the pointer
   * that opened the tooltip is no longer over it and no mouseleave arrives to shut it - it would
   * stay up, saying "Close chat" beside a tab that now opens the chat. Closed by hand instead.
   */
  @ViewChild('toggleButton', { read: NgbTooltip }) private toggleTooltip?: NgbTooltip;

  /**
   * One restore per app load. Without it, every later `isUserLoggedIn` emission - a token
   * refresh, a re-login - would reopen a panel the customer has since deliberately closed.
   */
  private hasRestoredOpenState = false;
  /* ################### Variable Declaration End ################### */

  constructor(
    store: AiChatConversationStore,
    aiChatService: AiChatService,
    toastService: ToastService,
    modalService: NgbModal,
    private authService: AuthService,
    private subscriberService: SubscribersService,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    super(store, aiChatService, toastService, modalService, platformId);

    this.isUserAuthenticated = this.authService.isUserAuthenticated();
    this.restoreOpenState();
    this.subscriberService.isUserLoggedIn
      .pipe(takeUntil(this.destroy$))
      .subscribe((isLoggedIn: boolean) => {
        const wasGuest = !this.isUserAuthenticated;
        this.isUserAuthenticated = isLoggedIn;
        // Signing out mid-conversation must not leave the panel or its history on screen - nor
        // the page locked behind a sheet that is no longer rendered. The stored open flag goes
        // with it: the next customer on this browser starts with a closed bubble.
        if (!isLoggedIn) {
          this.isOpen.set(false);
          this.setSheetScrollLock(false);
          this.persistOpenState(false);
          this.store.resetConversation();
          return;
        }
        // Signed in from the panel's own sign-in card (login does not reload the page): the open
        // panel turns into the chat in place, so carry on as a deliberate open would. Only on the
        // guest -> signed-in edge - a token refresh re-emits true and must not steal focus.
        if (wasGuest && this.isOpen()) {
          this.hasRestoredOpenState = true;
          this.persistOpenState(true);
          this.restoreConversation();
          this.focusComposer();
          this.scrollToLatest(true);
          return;
        }
        // Auth often arrives after construction - it is rebuilt from the /me call - so the
        // reload path lands here, not in the constructor.
        this.restoreOpenState();
      });
  }

  /**
   * Reopens the panel if it was open when this app last unloaded, transcript and all.
   *
   * The panel used to come back closed from every full load - a reply link (they open in a new
   * tab), a hard refresh, a URL typed over the page - even though the conversation itself was
   * saved. Reopening here is a continuation, not a new enquiry, so it goes through
   * ensureRestored() and puts the stored transcript back rather than applying toggleChat's
   * fresh-conversation policy - which stays as it is, because a deliberate open IS a new enquiry.
   */
  private restoreOpenState(): void {
    if (this.hasRestoredOpenState || !this.isUserAuthenticated || !isPlatformBrowser(this.platformId)) {
      return;
    }
    this.hasRestoredOpenState = true;

    let stored: string | null = null;
    try {
      stored = localStorage.getItem(STORAGE.AI_CHAT_PANEL_OPEN_KEY);
    } catch {
      return;
    }

    if (stored !== '1') {
      return;
    }

    this.isOpen.set(true);
    this.setSheetScrollLock(true);
    this.store.ensureRestored();
    this.scrollToLatest(true);
  }

  /**
   * Records whether the panel is open, so the next app load can put it back the way it was.
   * localStorage rather than sessionStorage because reply links open with rel="noopener" - the
   * new tab shares nothing session-scoped with this one.
   */
  private persistOpenState(isOpen: boolean): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      if (isOpen) {
        localStorage.setItem(STORAGE.AI_CHAT_PANEL_OPEN_KEY, '1');
      } else {
        localStorage.removeItem(STORAGE.AI_CHAT_PANEL_OPEN_KEY);
      }
    } catch {
      // Private mode or full storage: the panel just will not restore on the next load.
    }
  }

  /**
   * Opens or closes the panel. The conversation is created on first open so guests and users
   * who never open the widget cost nothing.
   */
  toggleChat(): void {
    const willOpen = !this.isOpen();
    this.toggleTooltip?.close();
    this.isOpen.set(willOpen);
    this.setSheetScrollLock(willOpen);
    // A guest's open panel is not remembered: it would otherwise pop open on some later visit,
    // the moment that browser next signs in.
    this.persistOpenState(willOpen && this.isUserAuthenticated);

    if (!willOpen) {
      return;
    }

    // Signed out there is no conversation to restore - only the sign-in card, which is where
    // keyboard focus belongs.
    if (!this.isUserAuthenticated) {
      this.focusGuestSignIn();
      return;
    }

    // OPENING THE PANEL CONTINUES THE CONVERSATION - it never starts one.
    //
    // This used to call startFreshConversation(), on the argument that the bubble is a "quick
    // question" surface reached from anywhere on the site, so what was asked an hour ago on some
    // other page is rarely what it is being opened to ask now. In practice that made the launcher
    // amnesiac: closing the panel to look at the product being discussed and reopening it threw
    // the thread away, which is the one moment a customer is most likely to close it. The agent's
    // memory is keyed by conversation_uuid, so a new one each time also meant re-explaining the
    // context on every reopen.
    //
    // THERE IS NOW EXACTLY ONE WAY TO START A THREAD: "New chat" in the full page's history rail
    // (store.startNewConversation). Because both surfaces read the same store, that one click is
    // what moves the widget onto the new thread too - which is the whole point of there being a
    // single conversation rather than one per surface.
    //
    // restoreConversation() is a no-op after the first surface has opened in this session - the
    // store already holds the conversation - so this only reads storage on the first open after a
    // page load, and can never rewind a turn that is still streaming.
    this.restoreConversation();
    this.focusComposer();
    this.scrollToLatest(true);
  }

  /**
   * Closes the panel and returns focus to the tab - closing from the panel's own
   * close button would otherwise drop focus to the top of the document.
   */
  closeChat(): void {
    this.toggleTooltip?.close();
    this.isOpen.set(false);
    this.setSheetScrollLock(false);
    this.persistOpenState(false);
    this.focusToggle();
  }

  /**
   * Opens the site's sign-in / registration modal from the guest card, the same AuthComponent
   * every other "Sign In" on the site opens. The panel stays open underneath (ngb modals sit
   * above it), so a successful sign-in lands the customer straight in the chat.
   */
  async openAuthModal(authModel: string): Promise<void> {
    const { AuthComponent } = await import('src/app/features/auth/auth.component');
    const modalRef = this.modalService.open(AuthComponent, {
      scrollable: true,
      modalDialogClass: 'auth-modal'
    });
    modalRef.componentInstance.authModel = authModel;
  }

  /**
   * Escape closes the panel from anywhere inside it - unless a modal (the image preview, the
   * feedback dialog) is open above it, in which case Escape belongs to the modal and should
   * peel one layer, not the whole stack.
   */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen() && !this.modalService.hasOpenModals()) {
      this.closeChat();
    }
  }

  /**
   * Locks the document behind the panel while it is a full-window sheet.
   *
   * The class is set at every width and the lock itself is applied in CSS below 992px, where the
   * panel covers the window - deciding it here would mean measuring the viewport in JS and
   * re-measuring on rotate. Without it, scrolling past the end of the conversation hands the
   * scroll to the page underneath, and closing the sheet lands the customer somewhere they never
   * navigated to.
   */
  private setSheetScrollLock(locked: boolean): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    document.body.classList.toggle('ai-chat-sheet-open', locked);
  }

  /**
   * Returns focus to the tab after the panel is dismissed.
   */
  private focusToggle(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    setTimeout(() => this.toggleButton?.nativeElement?.focus());
  }

  /**
   * Moves focus to the sign-in card's button once a guest has opened the panel - the panel
   * renders on the next change detection, hence the timeout.
   */
  private focusGuestSignIn(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    setTimeout(() => this.guestSignIn?.nativeElement?.focus());
  }
}
